import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import RealisticBrainBattery from './RealisticBrainBattery';

const Dashboard = ({ accountId }) => {
  // Debug logging
  console.log('Dashboard component rendered with accountId:', accountId);
  console.log('accountId type:', typeof accountId);
  console.log('accountId === 1:', accountId === 1);
  
  // Get userId from API profile
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Real data from database - dynamic metrics array
  const [metrics, setMetrics] = useState([]);
  
  // Widget customization state
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [visibleWidgets, setVisibleWidgets] = useState(() => {
    // Load from localStorage or default to all visible
    const saved = localStorage.getItem('dashboardVisibleWidgets');
    return saved ? JSON.parse(saved) : {};
  });

  const [sessionData, setSessionData] = useState({
    totalSessions: 0,
    totalFocusTime: '0h 0m',
    averageProductivity: 0,
    weeklyTrend: '0%',
  });

  const [topApps, setTopApps] = useState([]);

  const [weeklyData, setWeeklyData] = useState([]);
  
  // Chart metric selection state
  const [selectedChartMetrics, setSelectedChartMetrics] = useState(() => {
    const saved = localStorage.getItem('dashboardChartMetrics');
    return saved ? JSON.parse(saved) : [];
  });

  const [recentSessions, setRecentSessions] = useState([]);

  const [wellnessTrends, setWellnessTrends] = useState({
    averageTaskSwitching: 38,
    cognitiveBalance: 71, // Balance between System 1 and System 2
    stressReduction: '+15%', // Improvement over time
    focusImprovement: '+22%',
    mentalFatigueLevel: 28,
    workLifeBalance: 82,
  });

  // Get userId from profile API on mount
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const response = await fetch('/api/profile', {
          method: 'GET',
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Profile data for dashboard:', data);
          
          // Extract UserID from the profile response
          if (data.userId) {
            setUserId(data.userId);
            console.log('UserID set:', data.userId);
          } else {
            setError('User ID not found in profile data.');
            setLoading(false);
          }
        } else if (response.status === 401) {
          setError('Please log in to view your dashboard.');
          setLoading(false);
        } else {
          setError('Failed to fetch user profile.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching user ID:', err);
        setError('Error connecting to server.');
        setLoading(false);
      }
    };
    
    fetchUserId();
  }, []);

  // Fetch metrics from database
  useEffect(() => {
    if (!userId) return;

    const fetchMetrics = async () => {
      try {
        setLoading(true);
        
        // Fetch main metrics (now returns dynamic array)
        const metricsResponse = await fetch(`/api/user-metrics/${userId}`);
        if (!metricsResponse.ok) throw new Error('Failed to fetch metrics');
        const metricsData = await metricsResponse.json();
        
        // Set metrics array with exact names from database
        const allMetrics = metricsData.metrics || [];
        
        // Filter out unwanted metrics
        const excludedMetrics = [
          'longTermStressMetric',
          'attentionScore',
          'positiveScore',
          'healthScore',
          'burnoutRiskIndex',
          'psychologicalSafetyIndex',
          'productivityIndex',
          'staffHappinessIndex',
          'stressScore',
          'inactivityDuration'
        ];
        
        const fetchedMetrics = allMetrics.filter(metric => 
          !excludedMetrics.includes(metric.name)
        );
        
        setMetrics(fetchedMetrics);
        
        // Initialize visible widgets if not already set
        setVisibleWidgets(prev => {
          const current = Object.keys(prev).length > 0 ? prev : {};
          fetchedMetrics.forEach(metric => {
            if (current[metric.id] === undefined) {
              current[metric.id] = true; // Default to visible
            }
          });
          localStorage.setItem('dashboardVisibleWidgets', JSON.stringify(current));
          return current;
        });
        
        setSessionData(prev => ({
          ...prev,
          totalSessions: metricsData.totalSessions || 0,
          averageProductivity: metricsData.metrics?.find(m => m.name.toLowerCase().includes('productivity'))?.value || 0
        }));
        
        // Initialize selected chart metrics if empty
        setSelectedChartMetrics(prev => {
          if (prev.length === 0 && fetchedMetrics.length > 0) {
            // Default to first 3 metrics
            const defaultMetrics = fetchedMetrics.slice(0, 3).map(m => m.name);
            localStorage.setItem('dashboardChartMetrics', JSON.stringify(defaultMetrics));
            return defaultMetrics;
          }
          return prev;
        });
        
        // Fetch recent sessions
        const sessionsResponse = await fetch(`/api/user-metrics/${userId}/sessions`);
        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json();
          setRecentSessions(sessionsData);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching metrics:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchMetrics();
    
    // Refresh metrics every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, [userId]);
  
  // Toggle widget visibility
  const toggleWidget = (metricId) => {
    setVisibleWidgets(prev => {
      const updated = { ...prev, [metricId]: !prev[metricId] };
      localStorage.setItem('dashboardVisibleWidgets', JSON.stringify(updated));
      return updated;
    });
  };
  
  // Show all widgets
  const showAllWidgets = () => {
    const allVisible = {};
    metrics.forEach(metric => {
      allVisible[metric.id] = true;
    });
    setVisibleWidgets(allVisible);
    localStorage.setItem('dashboardVisibleWidgets', JSON.stringify(allVisible));
  };
  
  // Hide all widgets
  const hideAllWidgets = () => {
    const allHidden = {};
    metrics.forEach(metric => {
      allHidden[metric.id] = false;
    });
    setVisibleWidgets(allHidden);
    localStorage.setItem('dashboardVisibleWidgets', JSON.stringify(allHidden));
  };
  
  // Toggle chart metric selection
  const toggleChartMetric = (metricName) => {
    setSelectedChartMetrics(prev => {
      let updated;
      if (prev.includes(metricName)) {
        updated = prev.filter(m => m !== metricName);
      } else {
        // Limit to 5 metrics max for readability
        if (prev.length >= 5) {
          alert('You can select up to 5 metrics for the chart');
          return prev;
        }
        updated = [...prev, metricName];
      }
      localStorage.setItem('dashboardChartMetrics', JSON.stringify(updated));
      return updated;
    });
  };
  
  // Fetch weekly data when selected metrics change
  useEffect(() => {
    if (!userId || selectedChartMetrics.length === 0) return;
    
    const fetchWeeklyData = async () => {
      try {
        const metricParams = selectedChartMetrics.join(',');
        const weeklyResponse = await fetch(`/api/user-metrics/${userId}/weekly?metrics=${encodeURIComponent(metricParams)}`);
        if (weeklyResponse.ok) {
          const weeklyDataResult = await weeklyResponse.json();
          setWeeklyData(weeklyDataResult);
        }
      } catch (err) {
        console.error('Error fetching weekly data:', err);
      }
    };
    
    fetchWeeklyData();
  }, [userId, selectedChartMetrics]);

  const getMetricColor = (type, value) => {
    if (type === 'productivity' || type === 'system1' || type === 'system2') {
      // Higher is better
      if (value >= 70) return '#10b981'; // green
      if (value >= 40) return '#f59e0b'; // orange
      return '#ef4444'; // red
    } else {
      // For stress, attention residue, cognitive fatigue, task switching, long term stress (lower is better)
      if (value <= 30) return '#10b981'; // green
      if (value <= 60) return '#f59e0b'; // orange
      return '#ef4444'; // red
    }
  };

  const getMetricStatus = (type, value) => {
    if (type === 'productivity' || type === 'system1' || type === 'system2') {
      if (value >= 70) return 'Excellent';
      if (value >= 40) return 'Good';
      return 'Needs Attention';
    } else {
      if (value <= 30) return 'Excellent';
      if (value <= 60) return 'Moderate';
      return 'High';
    }
  };
  
  // Get color for chart bars based on index
  const getChartColor = (index) => {
    const colors = [
      '#3b82f6', // blue
      '#ef4444', // red
      '#10b981', // green
      '#f59e0b', // orange
      '#8b5cf6', // purple
      '#ec4899', // pink
      '#14b8a6', // teal
      '#f97316', // orange-red
    ];
    return colors[index % colors.length];
  };
  
  // Map database metric names to user-friendly display names
  const getDisplayName = (metricName) => {
    const nameMap = {
      'attentionResidueScore': 'Attention Residue',
      'system1Score': 'System 1',
      'system2Score': 'System 2',
      'newCortisolLevel': 'Cortisol Levels',
      'averageTypingFatigue': 'Cognitive Fatigue',
      'averageOverallLateralStress': 'Stress',
      'productivityScore': 'Productivity',
      'prefrontalCortexActivation': 'Prefrontal Cortex Activation',
      'frequencyOfTaskSwitching': 'Frequency of Task Switching'
    };
    return nameMap[metricName] || metricName;
  };

  // Check if user has access to dashboard (only accountId = 1)
  if (accountId !== 1) {
    return (
      <div className="dashboard-placeholder-container">
        <div className="dashboard-placeholder">
          <div className="placeholder-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <rect x="9" y="9" width="6" height="6"/>
              <path d="m9 1 0 6"/>
              <path d="m15 1 0 6"/>
              <path d="m9 17 0 6"/>
              <path d="m15 17 0 6"/>
              <path d="m1 9 6 0"/>
              <path d="m17 9 6 0"/>
              <path d="m1 15 6 0"/>
              <path d="m17 15 6 0"/>
            </svg>
          </div>
          <h3>Dashboard Coming Soon</h3>
          <p>We're working on something amazing! Stay tuned for powerful analytics, insights, and management tools that will help you get the most out of your experience.</p>
          <div className="coming-soon-badge">
            <span>🚀 Stay Tuned</span>
          </div>
        </div>
      </div>
    );
  }

  // Don't show loading screen - prevents page flash on refresh

  // Show error state
  if (error) {
    return (
      <div className="dashboard-placeholder-container">
        <div className="dashboard-placeholder">
          <div className="placeholder-icon" style={{ color: '#ef4444' }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ 
              marginTop: '20px', 
              padding: '10px 20px', 
              backgroundColor: '#3b82f6', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer' 
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="title-row">
            <div>
              <h1 className="dashboard-title">WhiteMatter Analytics Dashboard</h1>
              <p className="dashboard-subtitle">Real-time cognitive performance insights</p>
            </div>
            <button 
              className="customize-button"
              onClick={() => setShowCustomizer(!showCustomizer)}
              title="Customize Dashboard"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6m8.66-9.66l-5.2 3m-5.92 3.46l-5.2 3M1 12h6m6 0h6m-1.34 8.66l-3-5.2m-3.46-5.92l-3-5.2"/>
              </svg>
              Customize Widgets
            </button>
          </div>
        </div>
        <div className="header-stats">
          <div className="header-stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-value">{sessionData.totalSessions}</div>
              <div className="stat-label">Total Sessions</div>
            </div>
          </div>
          <div className="header-stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-content">
              <div className="stat-value">{sessionData.totalFocusTime}</div>
              <div className="stat-label">Focus Time</div>
            </div>
          </div>
          <div className="header-stat-card trend-positive">
            <div className="stat-icon">📈</div>
            <div className="stat-content">
              <div className="stat-value">{sessionData.weeklyTrend}</div>
              <div className="stat-label">Weekly Trend</div>
            </div>
          </div>
        </div>
      </div>

      {/* Brain Battery - 3D Cognitive Load Visualizer */}
      <RealisticBrainBattery metrics={metrics} />

      {/* Widget Customizer Panel */}
      {showCustomizer && (
        <div className="widget-customizer">
          <div className="customizer-header">
            <h3>Customize Your Dashboard</h3>
            <p>Select which metrics you want to display</p>
          </div>
          <div className="customizer-controls">
            <button className="control-btn" onClick={showAllWidgets}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Show All
            </button>
            <button className="control-btn" onClick={hideAllWidgets}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Hide All
            </button>
          </div>
          <div className="widget-toggles">
            {metrics.map((metric) => (
              <label key={metric.id} className="widget-toggle">
                <input
                  type="checkbox"
                  checked={visibleWidgets[metric.id] !== false}
                  onChange={() => toggleWidget(metric.id)}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-label">{getDisplayName(metric.name)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Main Metrics Grid - DYNAMIC */}
      <div className="metrics-grid">
        {metrics.length > 0 ? (
          metrics.filter(metric => visibleWidgets[metric.id] !== false).map((metric) => (
            <div key={metric.id} className="metric-card">
              <div className="metric-header">
                <div className="metric-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="metric-info">
                  <h3 className="metric-title">{getDisplayName(metric.name)}</h3>
                  <span className="metric-status" style={{ color: getMetricColor('default', metric.value) }}>
                    {getMetricStatus('default', metric.value)}
                  </span>
                </div>
              </div>
              <div className="metric-value-large">{metric.value}%</div>
              <div className="metric-progress-bar">
                <div 
                  className="metric-progress-fill" 
                  style={{ 
                    width: `${Math.min(metric.value, 100)}%`,
                    backgroundColor: getMetricColor('default', metric.value)
                  }}
                ></div>
              </div>
              <p className="metric-description">
                {metric.value >= 70 ? 'Excellent performance!' : 
                 metric.value >= 40 ? 'Good level, keep it up!' : 
                 'Consider taking action to improve.'}
              </p>
            </div>
          ))
        ) : metrics.length === 0 ? (
          <div className="no-metrics-message">
            <p>No metrics data available. Please check back later.</p>
          </div>
        ) : (
          <div className="no-metrics-message">
            <p>No widgets selected. Click "Customize Widgets" to choose metrics to display.</p>
          </div>
        )}
      </div>

      {/* OLD HARDCODED METRICS - REMOVED */}
      <div className="metrics-grid" style={{display: 'none'}}>
        <div className="metric-card productivity-card">
          <div className="metric-header">
            <div className="metric-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 12h2m4 0h2m4 0h2m4 0h2M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="metric-info">
              <h3 className="metric-title">Productivity</h3>
              <span className="metric-status" style={{ color: getMetricColor('productivity', metrics.productivity) }}>
                {getMetricStatus('productivity', metrics.productivity)}
              </span>
            </div>
          </div>
          <div className="metric-value-large">{Math.round(metrics.productivity)}%</div>
          <div className="metric-progress-bar">
            <div 
              className="metric-progress-fill" 
              style={{ 
                width: `${metrics.productivity}%`,
                backgroundColor: getMetricColor('productivity', metrics.productivity)
              }}
            ></div>
          </div>
          <p className="metric-description">
            {metrics.productivity >= 70 ? 'You\'re performing exceptionally well!' : 
             metrics.productivity >= 40 ? 'Good performance, keep it up!' : 
             'Consider taking a break to recharge.'}
          </p>
        </div>

        <div className="metric-card stress-card">
          <div className="metric-header">
            <div className="metric-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 5v2m0 10v2m7-7h-2M5 12h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="metric-info">
              <h3 className="metric-title">Stress Level</h3>
              <span className="metric-status" style={{ color: getMetricColor('stress', metrics.stress) }}>
                {getMetricStatus('stress', metrics.stress)}
              </span>
            </div>
          </div>
          <div className="metric-value-large">{Math.round(metrics.stress)}%</div>
          <div className="metric-progress-bar">
            <div 
              className="metric-progress-fill" 
              style={{ 
                width: `${metrics.stress}%`,
                backgroundColor: getMetricColor('stress', metrics.stress)
              }}
            ></div>
          </div>
          <p className="metric-description">
            {metrics.stress <= 30 ? 'Your stress levels are well managed.' : 
             metrics.stress <= 60 ? 'Moderate stress detected. Consider a breathing exercise.' : 
             'High stress levels. Take a break and practice mindfulness.'}
          </p>
        </div>

        <div className="metric-card attention-card">
          <div className="metric-header">
            <div className="metric-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 12h2m4 0h2m4 0h2m4 0h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M3 8h18M3 16h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="metric-info">
              <h3 className="metric-title">Attention Residue</h3>
              <span className="metric-status" style={{ color: getMetricColor('attention', metrics.attentionResidue) }}>
                {getMetricStatus('attention', metrics.attentionResidue)}
              </span>
            </div>
          </div>
          <div className="metric-value-large">{Math.round(metrics.attentionResidue)}%</div>
          <div className="metric-progress-bar">
            <div 
              className="metric-progress-fill" 
              style={{ 
                width: `${metrics.attentionResidue}%`,
                backgroundColor: getMetricColor('attention', metrics.attentionResidue)
              }}
            ></div>
          </div>
          <p className="metric-description">
            {metrics.attentionResidue <= 30 ? 'Excellent focus! Minimal task-switching.' : 
             metrics.attentionResidue <= 60 ? 'Moderate multitasking detected.' : 
             'High task-switching. Focus on one task at a time.'}
          </p>
        </div>

        <div className="metric-card fatigue-card">
          <div className="metric-header">
            <div className="metric-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="metric-info">
              <h3 className="metric-title">Cognitive Fatigue</h3>
              <span className="metric-status" style={{ color: getMetricColor('fatigue', metrics.cognitiveFatigue) }}>
                {getMetricStatus('fatigue', metrics.cognitiveFatigue)}
              </span>
            </div>
          </div>
          <div className="metric-value-large">{Math.round(metrics.cognitiveFatigue)}%</div>
          <div className="metric-progress-bar">
            <div 
              className="metric-progress-fill" 
              style={{ 
                width: `${metrics.cognitiveFatigue}%`,
                backgroundColor: getMetricColor('fatigue', metrics.cognitiveFatigue)
              }}
            ></div>
          </div>
          <p className="metric-description">
            {metrics.cognitiveFatigue <= 30 ? 'You\'re feeling fresh and energized!' : 
             metrics.cognitiveFatigue <= 60 ? 'Moderate fatigue. Consider a short break.' : 
             'High fatigue detected. Time for a longer break.'}
          </p>
        </div>

        <div className="metric-card switching-card">
          <div className="metric-header">
            <div className="metric-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="2"/>
                <polyline points="7.5 4.21 12 6.81 16.5 4.21" stroke="currentColor" strokeWidth="2"/>
                <polyline points="7.5 19.79 7.5 14.6 3 12" stroke="currentColor" strokeWidth="2"/>
                <polyline points="21 12 16.5 14.6 16.5 19.79" stroke="currentColor" strokeWidth="2"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="currentColor" strokeWidth="2"/>
                <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div className="metric-info">
              <h3 className="metric-title">Task Switching Frequency</h3>
              <span className="metric-status" style={{ color: getMetricColor('switching', metrics.taskSwitchingFrequency) }}>
                {getMetricStatus('switching', metrics.taskSwitchingFrequency)}
              </span>
            </div>
          </div>
          <div className="metric-value-large">{Math.round(metrics.taskSwitchingFrequency)}%</div>
          <div className="metric-progress-bar">
            <div 
              className="metric-progress-fill" 
              style={{ 
                width: `${metrics.taskSwitchingFrequency}%`,
                backgroundColor: getMetricColor('switching', metrics.taskSwitchingFrequency)
              }}
            ></div>
          </div>
          <p className="metric-description">
            {metrics.taskSwitchingFrequency <= 30 ? 'Excellent! You\'re maintaining deep focus.' : 
             metrics.taskSwitchingFrequency <= 60 ? 'Moderate switching. Try to minimize distractions.' : 
             'High switching detected. Focus on single-tasking.'}
          </p>
        </div>

        <div className="metric-card system1-card">
          <div className="metric-header">
            <div className="metric-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="metric-info">
              <h3 className="metric-title">System 1 (Intuitive)</h3>
              <span className="metric-status" style={{ color: getMetricColor('system1', metrics.system1Score) }}>
                {getMetricStatus('system1', metrics.system1Score)}
              </span>
            </div>
          </div>
          <div className="metric-value-large">{Math.round(metrics.system1Score)}%</div>
          <div className="metric-progress-bar">
            <div 
              className="metric-progress-fill" 
              style={{ 
                width: `${metrics.system1Score}%`,
                backgroundColor: getMetricColor('system1', metrics.system1Score)
              }}
            ></div>
          </div>
          <p className="metric-description">
            {metrics.system1Score >= 70 ? 'Strong intuitive thinking! Quick decision-making.' : 
             metrics.system1Score >= 40 ? 'Good intuitive responses. Keep it balanced.' : 
             'Low intuitive thinking. May need more practice.'}
          </p>
        </div>

        <div className="metric-card system2-card">
          <div className="metric-header">
            <div className="metric-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 16v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="metric-info">
              <h3 className="metric-title">System 2 (Analytical)</h3>
              <span className="metric-status" style={{ color: getMetricColor('system2', metrics.system2Score) }}>
                {getMetricStatus('system2', metrics.system2Score)}
              </span>
            </div>
          </div>
          <div className="metric-value-large">{Math.round(metrics.system2Score)}%</div>
          <div className="metric-progress-bar">
            <div 
              className="metric-progress-fill" 
              style={{ 
                width: `${metrics.system2Score}%`,
                backgroundColor: getMetricColor('system2', metrics.system2Score)
              }}
            ></div>
          </div>
          <p className="metric-description">
            {metrics.system2Score >= 70 ? 'Excellent analytical thinking! Deep reasoning active.' : 
             metrics.system2Score >= 40 ? 'Good analytical processing. Stay focused.' : 
             'Low analytical thinking. Consider taking a break.'}
          </p>
        </div>

        <div className="metric-card longstress-card">
          <div className="metric-header">
            <div className="metric-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18 17l-5-5-3 3-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="metric-info">
              <h3 className="metric-title">Long-Term Stress</h3>
              <span className="metric-status" style={{ color: getMetricColor('longstress', metrics.longTermStress) }}>
                {getMetricStatus('longstress', metrics.longTermStress)}
              </span>
            </div>
          </div>
          <div className="metric-value-large">{Math.round(metrics.longTermStress)}%</div>
          <div className="metric-progress-bar">
            <div 
              className="metric-progress-fill" 
              style={{ 
                width: `${metrics.longTermStress}%`,
                backgroundColor: getMetricColor('longstress', metrics.longTermStress)
              }}
            ></div>
          </div>
          <p className="metric-description">
            {metrics.longTermStress <= 30 ? 'Excellent! Your long-term stress is well managed.' : 
             metrics.longTermStress <= 60 ? 'Moderate long-term stress. Monitor your wellness.' : 
             'High long-term stress. Consider lifestyle changes.'}
          </p>
        </div>
      </div>

      {/* Weekly Trend Chart */}
      <div className="chart-section">
        <div className="section-header">
          <h2 className="section-title">Weekly Performance Trend</h2>
          <button 
            className="chart-customize-btn"
            onClick={() => setShowCustomizer(!showCustomizer)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="21" x2="4" y2="14"></line>
              <line x1="4" y1="10" x2="4" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12" y2="3"></line>
              <line x1="20" y1="21" x2="20" y2="16"></line>
              <line x1="20" y1="12" x2="20" y2="3"></line>
              <line x1="1" y1="14" x2="7" y2="14"></line>
              <line x1="9" y1="8" x2="15" y2="8"></line>
              <line x1="17" y1="16" x2="23" y2="16"></line>
            </svg>
            Select Metrics
          </button>
        </div>
        
        {/* Metric Selector */}
        <div className="chart-metric-selector">
          <p className="selector-label">Select metrics to display (up to 5):</p>
          <div className="metric-chips">
            {metrics.map((metric) => (
              <label key={metric.id} className={`metric-chip ${selectedChartMetrics.includes(metric.name) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedChartMetrics.includes(metric.name)}
                  onChange={() => toggleChartMetric(metric.name)}
                />
                <span>{getDisplayName(metric.name)}</span>
              </label>
            ))}
          </div>
        </div>
        
        {/* Dynamic Chart Legend */}
        {selectedChartMetrics.length > 0 && (
          <div className="chart-legend">
            {selectedChartMetrics.map((metricName, idx) => (
              <div key={metricName} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: getChartColor(idx) }}></div>
                <span>{getDisplayName(metricName)}</span>
              </div>
            ))}
          </div>
        )}
        
        <div className="chart-container">
          {weeklyData.length > 0 ? weeklyData.map((day, index) => (
            <div key={index} className="chart-bar-group">
              <div className="chart-bars">
                {selectedChartMetrics.map((metricName, metricIdx) => {
                  const value = day.metrics?.[metricName] || 0;
                  return (
                    <div 
                      key={metricName}
                      className="chart-bar" 
                      style={{ 
                        height: `${value}%`,
                        backgroundColor: getChartColor(metricIdx)
                      }}
                      title={`${getDisplayName(metricName)}: ${value}%`}
                    ></div>
                  );
                })}
              </div>
              <div className="chart-label">{day.day}</div>
            </div>
          )) : (
            <div className="no-chart-data">
              <p>Select metrics above to view weekly trends</p>
            </div>
          )}
        </div>
      </div>

      {/* Top Apps and Recent Sessions */}
      <div className="bottom-grid">
        <div className="top-apps-section">
          <h2 className="section-title">Top Applications</h2>
          <div className="apps-list">
            {topApps.map((app, index) => (
              <div key={index} className="app-item">
                <div className="app-rank">{index + 1}</div>
                <div className="app-icon">{app.icon}</div>
                <div className="app-details">
                  <div className="app-name">{app.name}</div>
                  <div className="app-time">{app.time}</div>
                </div>
                <div className="app-productivity">
                  <div className="productivity-badge" style={{ 
                    backgroundColor: getMetricColor('productivity', app.productivity) 
                  }}>
                    {app.productivity}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="recent-sessions-section">
          <h2 className="section-title">Recent Focus Sessions</h2>
          <div className="sessions-list">
            {recentSessions.map((session, index) => (
              <div key={index} className="session-item">
                <div className="session-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div className="session-details">
                  <div className="session-date">{session.date}</div>
                  <div className="session-duration">{session.duration}</div>
                </div>
                <div className="session-productivity">
                  <div className="productivity-score" style={{ 
                    color: getMetricColor('productivity', session.productivity) 
                  }}>
                    {session.productivity}%
                  </div>
                </div>
                <div className="session-status">
                  <div className="status-badge completed">✓</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Wellness Trends */}
      <div className="wellness-trends-section">
        <h2 className="section-title">Wellness & Cognitive Trends</h2>
        <div className="wellness-grid">
          <div className="wellness-card">
            <div className="wellness-icon">🔄</div>
            <div className="wellness-value">{wellnessTrends.averageTaskSwitching}%</div>
            <div className="wellness-label">Avg Task Switching</div>
            <div className="wellness-trend positive">↓ Improving</div>
          </div>
          <div className="wellness-card">
            <div className="wellness-icon">⚖️</div>
            <div className="wellness-value">{wellnessTrends.cognitiveBalance}%</div>
            <div className="wellness-label">Cognitive Balance</div>
            <div className="wellness-trend positive">System 1 & 2 Harmony</div>
          </div>
          <div className="wellness-card">
            <div className="wellness-icon">📉</div>
            <div className="wellness-value">{wellnessTrends.stressReduction}</div>
            <div className="wellness-label">Stress Reduction</div>
            <div className="wellness-trend positive">↓ This Week</div>
          </div>
          <div className="wellness-card">
            <div className="wellness-icon">🎯</div>
            <div className="wellness-value">{wellnessTrends.focusImprovement}</div>
            <div className="wellness-label">Focus Improvement</div>
            <div className="wellness-trend positive">↑ This Week</div>
          </div>
          <div className="wellness-card">
            <div className="wellness-icon">🧠</div>
            <div className="wellness-value">{wellnessTrends.mentalFatigueLevel}%</div>
            <div className="wellness-label">Mental Fatigue Level</div>
            <div className="wellness-trend positive">↓ Decreasing</div>
          </div>
          <div className="wellness-card">
            <div className="wellness-icon">🌟</div>
            <div className="wellness-value">{wellnessTrends.workLifeBalance}%</div>
            <div className="wellness-label">Work-Life Balance</div>
            <div className="wellness-trend positive">↑ Excellent</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
